import React from "react";

import { Box, Stack, Typography } from "@mui/material";

import CommonButton from "../../ui/inputs/CommonButton.js";

import { colors } from "../../../../theme/colors.js";

const LiveDrawCard = ({
  image,
  title,
  type,
  roundLabel = "Draw",
  roundNumber,
  numbers = [],
  countdown = "00 : 00 : 00",
  buttonText = "View Draw",
  onClick,
  accentColor = colors.primary.main,
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        position: "relative",
        width: "100%",
        minHeight: "310px",
        borderRadius: "20px",
        overflow: "hidden",
        background: "secondary.dark",
        border: (theme) =>
          `1px solid ${theme.custom.colors.alpha.white[95]}`,
        boxShadow: "0 12px 32px rgba(70,100,140,0.14)",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 20px 45px rgba(70,100,140,0.20)",
        },
        "&:hover .live-draw-card-background": {
          transform: "scale(1.08)",
        },
        "@media (max-width: 600px)": {
          height: "auto",
          minHeight: "190px",
          borderRadius: "14px",
        },
      }}
    >
      {/* Background Image Layer */}
      <Box
        className="live-draw-card-background"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${image})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          transform: "scale(1)",
          transition: "transform 0.8s ease",
          pointerEvents: "none",
          "@media (max-width: 600px)": {
            backgroundPosition: "center",
          },
        }}
      />

      {/* Glassmorphism Gradient Overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: (theme) =>
            `linear-gradient(180deg, ${theme.custom.colors.alpha.white[82]} 0%, ${theme.custom.colors.alpha.white[68]} 38%, ${theme.custom.colors.alpha.white[88]} 68%, ${theme.custom.colors.alpha.white[97]} 100%)`,
        }}
      />

      {/* Main Content Container */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          minHeight: "310px",
          display: "flex",
          flexDirection: "column",
          p: 2,
          boxSizing: "border-box",
          "@media (max-width: 600px)": {
            minHeight: "0",
            height: "100%",
            p: "9px 10px",
          },
        }}
      >
        {/* Top Header: Game Type & Live Badge */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Box
            sx={{
              px: 1.2,
              py: 0.45,
              borderRadius: "999px",
              backgroundColor: `${accentColor}14`,
              color: accentColor,
              fontSize: "9px",
              lineHeight: 1.2,
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              "@media (max-width: 600px)": {
                px: 0.9,
                py: 0.3,
                fontSize: "7px",
              },
            }}
          >
            {type}
          </Box>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.1,
              py: 0.45,
              borderRadius: "999px",
              backgroundColor: "rgba(32, 217, 151, 0.15)",
              color: "#20d997",
              fontSize: "9px",
              lineHeight: 1.2,
              fontWeight: 800,
              boxShadow: "0 4px 12px rgba(32,217,151,0.15)",
              backdropFilter: "blur(4px)",
              "@media (max-width: 600px)": {
                gap: 0.4,
                px: 0.8,
                py: 0.3,
                fontSize: "7px",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: "#20d997",
                boxShadow: "0 0 8px rgba(32,217,151,0.8)",
                "@media (max-width: 600px)": {
                  width: "4px",
                  height: "4px",
                },
              }}
            />
            Live
          </Box>
        </Stack>

        {/* Center Content: Title & Draw Information */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: "auto",
          }}
        >
          <Typography
            component="h3"
            sx={{
              mt: 2,
              textAlign: "center",
              fontSize: "20px",
              lineHeight: 1.2,
              fontWeight: 800,
              color: "custom.colors.home.navyDark",
              textShadow: (theme) =>
                `0 2px 12px ${theme.custom.colors.alpha.white[90]}`,
              "@media (max-width: 600px)": {
                mt: 0.4,
                fontSize: "15px",
                lineHeight: 1.1,
              },
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              mt: 0.45,
              textAlign: "center",
              fontSize: "10px",
              lineHeight: 1.2,
              color: "custom.colors.alpha.navy[55]",
              "@media (max-width: 600px)": {
                mt: 0.2,
                fontSize: "7px",
              },
            }}
          >
            {roundLabel} #{roundNumber}
          </Typography>

          {/* Lottery Numbers Mapping */}
          {numbers.length > 0 && (
            <Stack
              direction="row"
              justifyContent="center"
              spacing={0.7}
              sx={{
                mt: 1.2,
                flexWrap: "wrap",
                "@media (max-width: 600px)": {
                  mt: 0.5,
                  gap: "3px",
                },
              }}
            >
              {numbers.map((number, index) => {
                const isLast = index === numbers.length - 1;

                return (
                  <Box
                    key={`${number}-${index}`}
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isLast
                        ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`
                        : "rgba(235,243,255,0.95)",
                      color: isLast
                        ? colors.primary.contrastText
                        : colors.secondary.dark,
                      border: isLast
                        ? "none"
                        : "1px solid rgba(37,99,235,0.12)",
                      fontSize: "11px",
                      fontWeight: 800,
                      boxShadow: isLast
                        ? `0 5px 14px ${accentColor}35`
                        : "0 4px 10px rgba(70,100,140,0.08)",
                      backdropFilter: "blur(6px)",
                      WebkitBackdropFilter: "blur(6px)",
                      "@media (max-width: 600px)": {
                        width: "22px",
                        height: "22px",
                        fontSize: "8px",
                      },
                    }}
                  >
                    {number}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* Bottom Section: Countdown Timer Block */}
        <Box
          sx={{
            mt: 2,
            px: 1.5,
            py: 1.2,
            borderRadius: "14px",
            background: "rgba(238,245,255,0.78)",
            border: "1px solid rgba(37,99,235,0.06)",
            textAlign: "center",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            boxShadow: "0 8px 20px rgba(70,100,140,0.08)",
            "@media (max-width: 600px)": {
              mt: 0.6,
              px: 0.8,
              py: 0.35,
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            },
          }}
        >
          <Typography
            sx={{
              fontSize: "9px",
              lineHeight: 1.2,
              color: "custom.colors.alpha.navy[55]",
              mb: 0.5,
              "@media (max-width: 600px)": {
                fontSize: "6px",
                mb: 0,
                whiteSpace: "nowrap",
              },
            }}
          >
            Next Draw In
          </Typography>

          <Typography
            sx={{
              fontSize: "25px",
              lineHeight: 1.1,
              fontWeight: 800,
              letterSpacing: "0.04em",
              color: colors.secondary.dark,
              "@media (max-width: 600px)": {
                fontSize: "12px",
                whiteSpace: "nowrap",
              },
            }}
          >
            {countdown}
          </Typography>

          <Stack
            direction="row"
            justifyContent="center"
            spacing={3}
            sx={{
              mt: 0.4,
              "@media (max-width: 600px)": {
                display: "none",
              },
            }}
          >
            {["Hours", "Minutes", "Seconds"].map((label) => (
              <Typography
                key={label}
                sx={{
                  fontSize: "8px",
                  color: "custom.colors.alpha.navy[50]",
                }}
              >
                {label}
              </Typography>
            ))}
          </Stack>
        </Box>

        {/* Action Button */}
        <CommonButton
          variant="contained"
          size="small"
          fullWidth
          onClick={onClick}
          label={buttonText}
          sx={{
            mt: 1.4,
            minHeight: "38px",
            height: "38px",
            py: 0,
            px: 2,
            borderRadius: "8px",
            backgroundColor: accentColor,
            boxShadow: `0 6px 15px ${accentColor}35`,
            "&:hover": {
              backgroundColor: accentColor,
              filter: "brightness(0.95)",
            },
            "@media (max-width: 600px)": {
              mt: 0.5,
              minHeight: "24px",
              height: "24px",
              px: 1,
              borderRadius: "5px",
              fontSize: "8px",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default LiveDrawCard;